import { EmptyStateNoResults, type PageAction, Pagination, SearchInput, Tooltip } from "@octopusdeploy/design-system-components";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { Repository, ArgoCDGatewayResource, EnvironmentV2Resource, ListArgoCDApplicationsArgs, ListArgoCDApplicationsResponse, ProjectResource, TenantResource } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { isEqual } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { ActionChameleonAnchor } from "~/areas/ActionChameleonAnchor";
import type { ArgoCDInstancePageLoaderData } from "~/areas/infrastructure/components/ArgoCDInstances/ArgoCDInstancePageLoaderData";
import { ArgoCDApplicationsTable } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDApplicationsTable";
import { EmptyStateNoArgoCDApplications } from "~/areas/infrastructure/components/ArgoCDInstances/components/EmptyStateNoArgoCDApplications";
import { GenerateArgoCDAnnotationsDrawer } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDAnnotationsDrawer/GenerateArgoCDAnnotationsDrawer";
import { createGenerateAnnotationsPageAction } from "~/areas/infrastructure/components/ArgoCDInstances/createGenerateAnnotationsPageAction";
import { getEnvironmentsForArgoCDApplications } from "~/areas/infrastructure/components/ArgoCDInstances/getEnvironmentsForArgoCDApplications";
import { AdvancedFilters } from "~/components/AdvancedFilterLayout/AdvancedFilters";
import { AdvancedFilterCheckbox } from "~/components/AdvancedFilterLayout/index";
import { EnvironmentMultiSelect } from "~/components/MultiSelect/EnvironmentMultiSelect";
import { ProjectMultiSelect } from "~/components/MultiSelect/ProjectMultiSelect";
import { TenantMultiSelect } from "~/components/MultiSelect/TenantMultiSelect";
import type { ContentFiltersProps } from "~/components/PageContent/PageContent";
import { PageContent } from "~/components/PageContent/PageContent";
import { getValueInStorage } from "~/hooks/localStorageHelpers";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import useLocalStorage from "~/hooks/useLocalStorage";
type ArgoCDInstanceApplicationsPageProps = {
    loaderData: ArgoCDInstanceApplicationsPageLoaderData;
    queryParams: ArgoCDInstanceApplicationsQueryParams;
    setQueryParams: QueryParamValuesSetter<ArgoCDInstanceApplicationsQueryParams>;
};
export const defaultQueryParams: ArgoCDInstanceApplicationsQueryParams = {
    pageNumber: undefined,
    name: "",
    projectIds: [],
    environmentIds: [],
    tenantIds: [],
    hideUnannotated: undefined,
};
export type ArgoCDInstanceApplicationsQueryParams = {
    pageNumber: number | undefined;
    name: string;
    environmentIds: string[];
    projectIds: string[];
    tenantIds: string[];
    hideUnannotated?: boolean;
};
export type ArgoCDInstanceApplicationsPageLoaderData = ArgoCDInstancePageLoaderData & {
    applicationsResponse: ListArgoCDApplicationsResponse;
    projects: ProjectResource[];
    environments: EnvironmentV2Resource[];
    tenants: TenantResource[];
};
function buildListArgs(queryParams: ArgoCDInstanceApplicationsQueryParams, pageSize: number): ListArgoCDApplicationsArgs {
    const pageNumber = queryParams.pageNumber === undefined ? 1 : queryParams.pageNumber;
    const skip = (pageNumber - 1) * pageSize;
    return {
        skip,
        take: pageSize,
        partialName: queryParams.name,
        projectIds: queryParams.projectIds,
        environmentIds: queryParams.environmentIds,
        tenantIds: queryParams.tenantIds,
        hideUnannotated: queryParams.hideUnannotated || false,
    };
}
const appsPageSizeStorageKey = "Octopus.ArgoCDInstanceApplications.PageSize";
const defaultPageSize = 30;
export async function argoCDInstanceApplicationsPageLoader(repository: Repository, gatewayPromise: Promise<ArgoCDGatewayResource>, queryParams: ArgoCDInstanceApplicationsQueryParams, ephemeralEnvironmentsAreEnabled: boolean): Promise<ArgoCDInstanceApplicationsPageLoaderData> {
    const gateway = await gatewayPromise;
    const userPageSize = getValueInStorage(appsPageSizeStorageKey);
    const pageSize = userPageSize ? parseInt(userPageSize) : defaultPageSize;
    const listArgs = buildListArgs(queryParams, pageSize);
    const applicationsResponsePromise = repository.ArgoCDGateways.listApps(gateway.Id, listArgs);
    const projectsPromise = repository.Projects.all();
    const environmentsPromise = getEnvironmentsForArgoCDApplications(repository, ephemeralEnvironmentsAreEnabled);
    const tenantsPromise = repository.Tenants.all();
    //we do these awaits here so they launch in parallel
    return { gateway, applicationsResponse: await applicationsResponsePromise, projects: await projectsPromise, environments: (await environmentsPromise).Items, tenants: await tenantsPromise };
}
export const ArgoCDInstanceApplicationsPage = (props: ArgoCDInstanceApplicationsPageProps) => {
    const { loaderData, queryParams, setQueryParams } = props;
    const updateQueryParams = useCallback((updated: Partial<ArgoCDInstanceApplicationsQueryParams>) => setQueryParams((prev) => ({ ...prev, ...updated })), [setQueryParams]);
    const [showAnnotationsDrawer, setShowAnnotationsDrawer] = useState<boolean>(false);
    const [initiallySelectedHelmApplicationId, setInitiallySelectedHelmApplicationId] = useState<string | undefined>();
    const [initiallySelectedApplicationSourceIndex, setInitiallySelectedApplicationSourceIndex] = useState<number | undefined>();
    const [pageSize, setPageSize] = useLocalStorage(appsPageSizeStorageKey, defaultPageSize);
    const [nameSearch, setNameSearch] = useDebouncedExternalState(queryParams.name, (val) => updateQueryParams({ name: val }), 200);
    const { result, refetch } = useQuery((repo) => {
        const listArgs = buildListArgs(queryParams, pageSize);
        return repo.ArgoCDGateways.listApps(loaderData.gateway.Id, listArgs);
    }, [loaderData.gateway.Id, pageSize, queryParams], "Load Argo CD applications and data", {
        initialResult: loaderData.applicationsResponse,
    });
    const applications = result.Resources;
    const isFiltering = useMemo(() => !isEqual(queryParams, defaultQueryParams), [queryParams]);
    const hasNoApplications = applications.length === 0 && !isFiltering;
    const hasNoFilteredApplications = applications.length === 0 && isFiltering;
    const additionalActions: PageAction[] = [createGenerateAnnotationsPageAction(() => setShowAnnotationsDrawer(true), hasNoApplications)];
    const filters: ContentFiltersProps | undefined = useMemo(() => {
        if (hasNoApplications) {
            return;
        }
        return {
            inputs: [<SearchInput value={nameSearch} onChange={setNameSearch} accessibleName={"Search Argo CD applications"} placeholder={"Filter by application name..."}/>],
            advancedFilters: {
                content: (<AdvancedFilters>
                        <ProjectMultiSelect accessibleName="projectFilter" key="filterProject" items={loaderData.projects} value={queryParams.projectIds} onChange={(x) => updateQueryParams({ projectIds: x })}/>
                        <EnvironmentMultiSelect accessibleName="environmentFilter" key="filterEnvironment" items={loaderData.environments} value={queryParams.environmentIds} onChange={(x) => updateQueryParams({ environmentIds: x })}/>
                        <TenantMultiSelect accessibleName="tenantFilter" key="filterTenant" items={loaderData.tenants} value={queryParams.tenantIds} onChange={(x) => updateQueryParams({ tenantIds: x })}/>
                        <Tooltip content={"Hides all applications that don't have Octopus annotations."}>
                            <AdvancedFilterCheckbox label={"Hide unannotated applications"} value={queryParams.hideUnannotated === undefined ? false : queryParams.hideUnannotated} onChange={(x) => updateQueryParams({ hideUnannotated: x ? true : undefined })}/>
                        </Tooltip>
                    </AdvancedFilters>),
                onResetFilter: () => updateQueryParams(defaultQueryParams),
                hasUserSelectedValues: isFiltering,
            },
        };
    }, [
        hasNoApplications,
        isFiltering,
        loaderData.environments,
        loaderData.projects,
        loaderData.tenants,
        nameSearch,
        queryParams.environmentIds,
        queryParams.hideUnannotated,
        queryParams.projectIds,
        queryParams.tenantIds,
        setNameSearch,
        updateQueryParams,
    ]);
    return (<PageContent header={{
            title: "Applications",
            pageActions: additionalActions,
        }} filters={filters} callout={{ type: "custom", content: <ActionChameleonAnchor actionTypes={["Octopus.ArgoCD.ImageUpdate"]}/> }} pagination={{
            ui: (<Pagination label={"Applications"} itemsPerPageOptions={[30, 50, 100]} currentPage={queryParams.pageNumber || 1} totalResults={result.FilteredCount || result.TotalCount} selectedItemsPerPage={pageSize} onPageChange={(page) => updateQueryParams({ pageNumber: page })} onPageSizeChange={setPageSize}/>),
            placement: "top",
        }}>
            {hasNoApplications && <EmptyStateNoArgoCDApplications />}
            {hasNoFilteredApplications && <EmptyStateNoResults />}
            {!hasNoApplications && !hasNoFilteredApplications && (<ArgoCDApplicationsTable {...loaderData} applicationsResponse={result} showAnnotationsDrawer={(selectedApplicationId, selectedApplicationSourceId) => {
                setInitiallySelectedHelmApplicationId(selectedApplicationId);
                setInitiallySelectedApplicationSourceIndex(selectedApplicationSourceId);
                setShowAnnotationsDrawer(true);
            }} onGitCredentialAdded={() => refetch()}/>)}
            <GenerateArgoCDAnnotationsDrawer spaceId={loaderData.gateway.SpaceId} gatewayId={loaderData.gateway.Id} gatewayName={loaderData.gateway.Name} sourcePage="Applications" initiallySelectedApplicationId={initiallySelectedHelmApplicationId} initiallySelectedApplicationSourceIndex={initiallySelectedApplicationSourceIndex} open={showAnnotationsDrawer} onClose={() => {
            setShowAnnotationsDrawer(false);
            setInitiallySelectedHelmApplicationId(undefined);
            setInitiallySelectedApplicationSourceIndex(undefined);
        }}/>
        </PageContent>);
};
